import { FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import { ComponentProps, ReactNode } from 'react';
import { BeneficiaryCard } from '../../../design-system/beneficiary-card/beneficiary-card';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { NameWithLogo } from '../../../design-system/name-with-logo/name-with-logo';
import { Stack } from '../../../design-system/stack/stack';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';

type ReviewStepProps = {
  name: string;
  amount: string;
  currency: SupportedCountryCurrency;
  accountDetails: ComponentProps<typeof BeneficiaryCard>['accountDetails'];
  latestPayeeCheck: ComponentProps<typeof BeneficiaryCard>['latestPayeeCheck'];
  bankName: string;
  bankLogo: string;
  reference: string;
  isLoading: boolean;
  onConfirm: () => Promise<void>;
  sourceAccountAdapter?: FinancialServiceProviderAdapters;
  customLabels?: {
    toLabel?: string;
    paymentDetailsLabel?: string;
    ctaText?: string;
  };
};

export const ReviewStep = ({
  name,
  amount,
  currency,
  accountDetails,
  latestPayeeCheck,
  bankName,
  bankLogo,
  reference,
  isLoading,
  onConfirm,
  sourceAccountAdapter,
  customLabels,
}: ReviewStepProps) => {
  const reviewLabels = getReviewLabels(customLabels);

  return (
    <Stack align="stretch" space={3}>
      <Stack align="stretch" space={2}>
        <p className="text-base font-medium text-black">{reviewLabels.toLabel}</p>

        <BeneficiaryCard
          name={name}
          currency={currency}
          accountDetails={accountDetails}
          latestPayeeCheck={latestPayeeCheck}
        />
      </Stack>

      <Stack align="stretch" space={2}>
        <p className="text-base font-medium text-black">{reviewLabels.paymentDetailsLabel}</p>

        <ReviewItem label="From" value={<NameWithLogo name={bankName} logo={bankLogo} logoSize="lg" />} />
      </Stack>

      <ReviewItem label="Amount" value={amount} />

      {/* Prevents showing empty reference field */}
      {!!reference && <ReviewItem label="Reference" value={reference} />}

      {sourceAccountAdapter === FinancialServiceProviderAdapters.YAPILY && (
        <div className="space-y-3 rounded-lg bg-muted p-4">
          <p className="text-sm text-muted-foreground">
            We have partnered with Yapily Connect to securely initiate payment from your account at {bankName}.
          </p>

          <p className="text-sm text-muted-foreground">
            By using the service, you agree to Yapily Connect initiating this payment and its{' '}
            <a
              href="https://www.yapily.com/legal/end-user-terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link underline"
            >
              Terms & Conditions
            </a>{' '}
            and{' '}
            <a
              href="https://www.yapily.com/legal/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link underline"
            >
              Privacy Notice
            </a>
            .
          </p>
        </div>
      )}

      <Button variant="default" size="lg" onClick={onConfirm} isLoading={isLoading}>
        {reviewLabels.ctaText}
      </Button>
    </Stack>
  );
};

type ReviewItemProps = {
  label: string;
  value: string | ReactNode;
};

const ReviewItem = ({ label, value }: ReviewItemProps) => {
  return (
    <Inline justify="between" className="rounded-lg border border-border bg-white p-5">
      <p className="text-base font-medium text-muted-foreground">{label}</p>

      {typeof value === 'string' ? <p className="text-base font-medium text-black">{value}</p> : value}
    </Inline>
  );
};

const getReviewLabels = (labelConfig?: ReviewStepProps['customLabels']) => {
  return {
    toLabel: labelConfig?.toLabel ?? 'Transfer to',
    paymentDetailsLabel: labelConfig?.paymentDetailsLabel ?? 'Transfer details',
    ctaText: labelConfig?.ctaText ?? 'Send transfer',
  };
};
